import { Component } from "react";

/**
 * Top-level error boundary so a render crash in any feature page never
 * leaves the user staring at a blank screen.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Lightweight client log — surfaces in console / DevTools for triage.
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  handleReload = () => {
    try { window.location.reload(); } catch (_e) { /* noop */ }
  };

  handleHome = () => {
    try { window.location.href = "/"; } catch (_e) { /* noop */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div
          className="w-full max-w-[440px] rounded-[22px] border border-border bg-card p-8 text-center shadow-[0_28px_60px_-22px_rgba(15,23,42,0.30)]"
          role="alert"
        >
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-[#B91C1C] dark:bg-rose-950/40">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6">
              <path d="M12 9v4" />
              <path d="M12 17h.01" />
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            </svg>
          </div>
          <h1 className="font-display text-[20px] font-extrabold tracking-tight">Something went wrong</h1>
          <p className="mt-2 text-[13.5px] text-muted-foreground">
            We hit an unexpected error. Reloading usually fixes it. Your data is safe.
          </p>
          <div className="mt-5 flex justify-center gap-2">
            <button
              type="button"
              onClick={this.handleReload}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-primary px-4 text-[13px] font-bold text-primary-foreground shadow-[0_12px_24px_-10px_hsl(var(--primary)/0.55)] hover:bg-primary/90"
            >
              Reload page
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-border bg-card px-4 text-[13px] font-bold hover:bg-muted/50"
            >
              Go home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
